import { createService } from "./createService"
import { SERVICE_INSTANCES } from "./serviceInstances"

const service = createService(SERVICE_INSTANCES.employee)

export const EMPLOYEE_INSTANCE = service.instanceKey

export const EMPLOYEE_ENDPOINTS = {
  // Personal info
  PERSONAL_INFO: "/employee/details/view/index_personal_info",
  OTHER_INFO: "/employee/details/view/index_other_info",
  CUSTOM_FIELD_INFO: "/employee/details/view/index_custom_field_info",
  UPDATE_DETAILS: "/employee/details/update",
  UPDATE_NEXT_OF_KIN: "/employee/people/update_nextofkin",
  UPDATE_HOME_ADDRESS: "/employee/people/update_homeaddress",
  PEOPLE_SELECT: "/employee/people/select",
  GET_ORG_CHART: "/employee/people/getorgchart",
  SAVE_CUSTOM_FIELD: "/employee/details/save_customfield",
  UPLOAD_CUSTOM_FIELD_DOCUMENT: "/employee/details/upload_customfield_document",
  DELETE_CUSTOM_FIELD_DOCUMENT: "/employee/details/delete_customfield_document",
  UPLOAD_IMAGE: "/employee/details/upload_image",
  PERSONAL_INFO_FOR_LOAN:
    "/employee/details/view/personalinfoforexternalloanrequest",
  MILESTONE_EMAIL_EXEMPTION: "/employee/milestoneemailexemptions/view/index",
  CREATE_MILESTONE_EMAIL_EXEMPTION: "/employee/milestoneemailexemptions/create",
  DELETE_MILESTONE_EMAIL_EXEMPTION: "/employee/milestoneemailexemptions/delete",

  // Job & Pay
  COMPENSATION_INFO: "/employee/job_and_pay/view/index_compensation_info",
  UPDATE_PREFERRED_ACCOUNT: "/employee/job_and_pay/update_preferred_account",
  UPDATE_PRIMARY_ACCOUNT: "/employee/job_and_pay/update_primary_account",
  UPDATE_SECONDARY_ACCOUNT: "/employee/job_and_pay/update_secondary_account",
  UPDATE_TAX: "/employee/job_and_pay/update_tax",
  UPDATE_PAYMENT_METHOD: "/employee/people/update_payment_method",
  ADD_RENT_PAYMENT: "/employee/job_and_pay/add_rent_payment",
  SELECT_RENT_PAYMENTS: "/employee/job_and_pay/selectrentpayments",

  // Payables
  SELECT_PAYABLES: "/employee/payables/select",
  ADD_PAYABLE: "/employee/payables/add",
  GET_PAYABLE: "/employee/payables/get",
  UPDATE_PAYABLE: "/employee/payables/update",
  GET_PAYABLE_TYPES: "/employee/payables/getpayabletypes",
  UPLOAD_PAYABLE_FILE: "/employee/payables/upload_file",

  // Documents
  ACKNOWLEDGE_DOCUMENT: "/employee/documents/acknowledge",

  // Payslips
  PAYSLIPS: "/employee/payslips/view/index",
  GET_PAYROLL_YEARS: "/employee/payslips/getpayrollyears",
  VIEW_CONSOLIDATED_PAYSLIPS: "/employee/payslips/view/consolidate_payslips",
  VIEW_PAYSLIP_DETAIL: "/payroll_items/view/payslip",
  VIEW_ALL_PAYSLIPS: "/payroll_items/view/payslips",

  // Notifications
  SELECT_NOTIFICATIONS: "/employee/notifications/select",
  VIEW_NOTIFICATION_DETAIL: "/notifications/view/detail",

  PENDING_PROFILE_UPDATES: "employee/profileupdates/pendingreviews",
} as const

export const employeeService = {
  instanceKey: service.instanceKey,

  // Personal
  getPersonalInfo: () => service.get(EMPLOYEE_ENDPOINTS.PERSONAL_INFO),
  getOtherInfo: () => service.get(EMPLOYEE_ENDPOINTS.OTHER_INFO),
  getCustomFieldInfo: () => service.get(EMPLOYEE_ENDPOINTS.CUSTOM_FIELD_INFO),
  updateDetails: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_DETAILS, body),
  updateNextOfKin: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_NEXT_OF_KIN, body),
  updateHomeAddress: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_HOME_ADDRESS, body),
  saveCustomField: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.SAVE_CUSTOM_FIELD, body),
  uploadCustomFieldDocument: (formData: FormData) =>
    service.upload(EMPLOYEE_ENDPOINTS.UPLOAD_CUSTOM_FIELD_DOCUMENT, formData),
  deleteCustomFieldDocument: (id: string | number) =>
    service.post(
      `${EMPLOYEE_ENDPOINTS.DELETE_CUSTOM_FIELD_DOCUMENT}?id=${id}`,
      { id }
    ),
  uploadImage: (formData: FormData, params?: Record<string, any>) =>
    service.upload(EMPLOYEE_ENDPOINTS.UPLOAD_IMAGE, formData, params),
  getPersonalInfoForLoan: (id?: string | number) =>
    service.get(
      EMPLOYEE_ENDPOINTS.PERSONAL_INFO_FOR_LOAN,
      id != null ? { id } : undefined
    ),
  getMilestoneEmailExemption: (id: string, type: number) =>
    service.get(EMPLOYEE_ENDPOINTS.MILESTONE_EMAIL_EXEMPTION, { id, type }),
  createMilestoneEmailExemption: (id: string, type: number) =>
    service.post(
      `${EMPLOYEE_ENDPOINTS.CREATE_MILESTONE_EMAIL_EXEMPTION}?id=${id}&type=${type}`
    ),
  deleteMilestoneEmailExemption: (id: string, type: number) =>
    service.delete(
      `${EMPLOYEE_ENDPOINTS.DELETE_MILESTONE_EMAIL_EXEMPTION}?id=${id}&type=${type}`
    ),

  // Job & Pay
  getCompensationInfo: () => service.get(EMPLOYEE_ENDPOINTS.COMPENSATION_INFO),
  updatePreferredAccount: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_PREFERRED_ACCOUNT, body),
  updatePrimaryAccount: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_PRIMARY_ACCOUNT, body),
  updateSecondaryAccount: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_SECONDARY_ACCOUNT, body),
  updateTax: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_TAX, body),
  updatePaymentMethod: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.UPDATE_PAYMENT_METHOD, body),
  addRentPayment: (id: string | number, body: Record<string, any>) =>
    service.post(`${EMPLOYEE_ENDPOINTS.ADD_RENT_PAYMENT}?id=${id}`, body),
  uploadRentPayment: (formData: FormData, id?: string | number) =>
    service.upload(
      id
        ? `${EMPLOYEE_ENDPOINTS.ADD_RENT_PAYMENT}?id=${id}`
        : EMPLOYEE_ENDPOINTS.ADD_RENT_PAYMENT,
      formData
    ),
  getRentPayments: (params?: {
    pageNumber?: number
    pageSize?: number
    searchParams?: string
    [key: string]: any
  }) => service.get(EMPLOYEE_ENDPOINTS.SELECT_RENT_PAYMENTS, params),

  // Notifications
  selectNotifications: (params?: { pageNumber?: number; pageSize?: number }) =>
    service.get(EMPLOYEE_ENDPOINTS.SELECT_NOTIFICATIONS, params),
  viewNotificationDetail: (id: string | number) =>
    service.get(EMPLOYEE_ENDPOINTS.VIEW_NOTIFICATION_DETAIL, { id }),

  // Payables
  selectPayables: (params?: {
    statusId?: string | number
    pageNumber?: number
    pageSize?: number
    searchParams?: string
    [key: string]: any
  }) => service.get(EMPLOYEE_ENDPOINTS.SELECT_PAYABLES, params),
  addPayable: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.ADD_PAYABLE, body),
  getPayable: (id: string | number) =>
    service.get(EMPLOYEE_ENDPOINTS.GET_PAYABLE, { id }),
  updatePayable: (id: string | number, body: Record<string, any>) =>
    service.post(`${EMPLOYEE_ENDPOINTS.UPDATE_PAYABLE}?id=${id}`, body),
  getPayableTypes: () => service.get(EMPLOYEE_ENDPOINTS.GET_PAYABLE_TYPES),
  uploadPayableFile: (id: string | number, formData: FormData) =>
    service.upload(
      `${EMPLOYEE_ENDPOINTS.UPLOAD_PAYABLE_FILE}?id=${id}`,
      formData
    ),

  // Documents
  acknowledgeDocument: (body: Record<string, any>) =>
    service.post(EMPLOYEE_ENDPOINTS.ACKNOWLEDGE_DOCUMENT, body),

  peopleSelect: (params?: Record<string, any>) =>
    service.get(EMPLOYEE_ENDPOINTS.PEOPLE_SELECT, params),
  getOrgChart: (params?: Record<string, any>) =>
    service.get(EMPLOYEE_ENDPOINTS.GET_ORG_CHART, params),
  getPayslips: (year: number) =>
    service.get(EMPLOYEE_ENDPOINTS.PAYSLIPS, { id: year }),
  getPayrollYears: () => service.get(EMPLOYEE_ENDPOINTS.GET_PAYROLL_YEARS),
  viewConsolidatedPayslips: (year: string | number) =>
    service.get(EMPLOYEE_ENDPOINTS.VIEW_CONSOLIDATED_PAYSLIPS, { id: year }),
  viewPayslipDetail: (id: string | number) =>
    service.get(EMPLOYEE_ENDPOINTS.VIEW_PAYSLIP_DETAIL, { id }),
  viewAllPayslips: (year: string | number) =>
    service.get(EMPLOYEE_ENDPOINTS.VIEW_ALL_PAYSLIPS, { id: year }),
  // Paging, search and status come from the table hook; without forwarding
  // them the list always returned page one, unfiltered.
  getPendingProfileUpdates: (params?: Record<string, any>) =>
    service.get(EMPLOYEE_ENDPOINTS.PENDING_PROFILE_UPDATES, params),
}
